import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { CheckCircle2, XCircle, Loader2, FileText, Eraser, PenLine, AlertTriangle, ShieldCheck, Lock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PublicEstimateView — public-facing signature page for invoices AND estimates.
 *
 * URL: /estimate/:token  OR  /invoice/:token (both routed here).
 * The backend `/api/invoices/public/{token}` returns `document_type` so the
 * UI can switch labels (Estimate / Invoice) without separate components.
 *
 * Flow:
 *   review → signing (capture name+email+signature) → submitting → signed
 *   review → rejecting (estimate only)            → submitting → rejected
 * Once signed/approved/declined, the page renders the final state from the
 * loaded document (idempotent — re-loading the URL after signing won't show
 * the form again).
 */
const PublicEstimateView = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Flow states: review | signing | rejecting | approved | rejected | submitting
  const [flowState, setFlowState] = useState("review");
  const [rejectReason, setRejectReason] = useState("");
  const [hasSigned, setHasSigned] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [redirecting, setRedirecting] = useState(false);
  const sigRef = useRef(null);

  const pdfUrl = `${API}/api/invoices/public/${token}/pdf`;

  useEffect(() => {
    fetch(`${API}/api/invoices/public/${token}`)
      .then(r => { if (!r.ok) throw new Error("Document not found"); return r.json(); })
      .then(d => {
        setData(d);
        // Already approved/signed BEFORE this visit → open the final PDF
        // directly (same behavior as paid invoices on the /pay link).
        if (d.signature_status === "signed" || d.status === "accepted") {
          setRedirecting(true);
          window.location.replace(`${API}/api/invoices/public/${token}/pdf`);
          return;
        }
        if (d.status === "declined") setFlowState("rejected");
        // Pre-fill signer fields from the customer record so the customer doesn't retype.
        if (d.customer?.name) setSignerName(d.customer.name);
        if (d.customer?.email) setSignerEmail(d.customer.email);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const clearSig = useCallback(() => {
    sigRef.current?.clear();
    setHasSigned(false);
  }, []);

  const handleApprove = async () => {
    if (!hasSigned) return;
    if (!signerName.trim()) { setError("Please enter your full name."); return; }
    if (!signerEmail.trim() || !/^\S+@\S+\.\S+$/.test(signerEmail.trim())) {
      setError("Please enter a valid email address."); return;
    }
    setError(null);
    setFlowState("submitting");
    try {
      const sigData = sigRef.current.toDataURL("image/png");
      const res = await fetch(`${API}/api/invoices/public/${token}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          signature_data: sigData,
          signer_name: signerName.trim(),
          signer_email: signerEmail.trim(),
        }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Failed"); }
      // Re-fetch so the success screen renders with the just-persisted audit
      // info (signed_at, signer fields) — keeps the UI honest. If the re-fetch
      // itself hiccups (network flap after a successful POST), don't flip the
      // user back to "signing" — that leads to a confusing "already signed"
      // error on their next attempt. Fall through to the success screen with
      // the client-known data instead.
      try {
        const refresh = await fetch(`${API}/api/invoices/public/${token}`);
        if (refresh.ok) setData(await refresh.json());
      } catch { /* keep the just-submitted client-side data */ }
      setFlowState("approved");
    } catch (e) { setError(e.message); setFlowState("signing"); }
  };

  const handleReject = async () => {
    setFlowState("submitting");
    try {
      const res = await fetch(`${API}/api/invoices/public/${token}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: rejectReason }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Failed"); }
      setFlowState("rejected");
    } catch (e) { setError(e.message); setFlowState("rejecting"); }
  };

  const fmt = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return d; } };

  if (loading) return (
    <div style={S.page}><div style={S.center}><Loader2 className="w-8 h-8 animate-spin" style={{ color: "#64748b" }} /></div></div>
  );
  if (redirecting) return (
    <div style={S.page} data-testid="estimate-pdf-redirect">
      <div style={{ ...S.card, textAlign: "center", padding: 40 }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#64748b", margin: "0 auto 14px" }} />
        <p style={S.sub}>Opening your {(data?.document_type || "estimate") === "estimate" ? "approved estimate" : "signed invoice"} (PDF)…</p>
        <a href={pdfUrl} style={{ fontSize: 13, color: "#2563eb", display: "inline-block", marginTop: 10 }}>Tap here if it doesn't open</a>
      </div>
    </div>
  );
  if (error && !data) return (
    <div style={S.page}><div style={{ ...S.card, textAlign: "center", padding: 40 }}>
      <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: "#ef4444" }} />
      <p style={{ color: "#ef4444", fontSize: 15, fontWeight: 500 }}>{error}</p>
    </div></div>
  );

  const { business, customer, branding } = data;
  const primary = branding?.primary_color || "#0f172a";
  const isEstimate = (data.document_type || "estimate") === "estimate";
  const docLabel = isEstimate ? "Estimate" : "Invoice";
  const docLabelUpper = docLabel.toUpperCase();

  // The submit/sign action is gated by the doc being either:
  //   - estimate in `sent` status (legacy approve flow), OR
  //   - any doc that has been Sent for Signature (signature_status='sent_for_signature'),
  //     OR the owner just shared the link before signing was requested.
  // We render the action bar whenever the doc isn't already signed/declined.
  const canSign = flowState !== "approved" && flowState !== "rejected" && data.signature_status !== "signed" && !data.signature_expired;

  // ─── EXPIRED LINK STATE ───
  if (data.signature_expired && flowState !== "approved" && flowState !== "rejected") return (
    <div style={S.page} data-testid="signature-expired-screen">
      <div style={{ ...S.card, textAlign: "center", padding: "48px 32px" }}>
        <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#fffbeb", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
          <AlertTriangle style={{ width: 36, height: 36, color: "#d97706" }} />
        </div>
        <h1 style={{ ...S.h1, marginBottom: 8 }}>Signature Link Expired</h1>
        <p style={S.sub}>
          This signature request for {docLabel.toLowerCase()} <strong>{data.invoice_number}</strong> has expired.
        </p>
        <p style={{ fontSize: 13, color: "#64748b", marginTop: 16 }}>
          Please contact {business?.name || "the business"}{business?.phone ? ` at ${business.phone}` : ""} to request a new signature link.
        </p>
      </div>
    </div>
  );

  // ─── APPROVED / SIGNED STATE ───
  if (flowState === "approved") return (
    <div style={S.page} data-testid="estimate-approved-screen">
      <div style={{ ...S.card, textAlign: "center", padding: "48px 32px" }}>
        <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#ecfdf5", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
          <CheckCircle2 style={{ width: 36, height: 36, color: "#059669" }} />
        </div>
        <h1 style={{ ...S.h1, marginBottom: 8 }}>{isEstimate ? "Estimate Approved" : "Invoice Signed"}</h1>
        <p style={S.sub}>Thank you! Your {isEstimate ? "approval" : "signature"} for <strong>{data.invoice_number}</strong> has been recorded.</p>
        <div style={{ ...S.totalBox, margin: "24px auto 0", maxWidth: 240 }}>
          <span style={S.totalLabel}>{isEstimate ? "Approved Total" : "Total"}</span>
          <span style={{ ...S.totalValue, color: "#059669" }}>{fmt(data.total)}</span>
        </div>
        {data.signature_data && (
          <div style={{ marginTop: 24 }}>
            <p style={{ ...S.label, textAlign: "center" }}>Your Signature</p>
            <img src={data.signature_data} alt="Signature" style={{ maxWidth: 220, height: 80, objectFit: "contain", margin: "8px auto", display: "block", opacity: 0.8 }} data-testid="signed-signature-image" />
          </div>
        )}
        {(data.signature_signer_name || data.signature_signed_at) && (
          <div style={{ marginTop: 16, fontSize: 12, color: "#64748b", lineHeight: 1.6 }} data-testid="signature-audit">
            {data.signature_signer_name && <div><strong>{data.signature_signer_name}</strong></div>}
            {data.signature_signer_email && <div>{data.signature_signer_email}</div>}
            {data.signature_signed_at && <div>Signed on {new Date(data.signature_signed_at).toLocaleString()}</div>}
          </div>
        )}
        <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 28 }}>A confirmation has been sent to {business?.name || "the business"}</p>
        <button
          onClick={() => { window.location.href = pdfUrl; }}
          style={{ ...S.btnPrimary, background: "#0f172a", marginTop: 20, display: "inline-flex", alignItems: "center", gap: 8 }}
          data-testid="view-approved-pdf-btn"
        >
          <FileText style={{ width: 16, height: 16 }} /> View {docLabel} (PDF)
        </button>
      </div>
    </div>
  );

  // ─── REJECTED STATE ───
  if (flowState === "rejected") return (
    <div style={S.page} data-testid="estimate-rejected-screen">
      <div style={{ ...S.card, textAlign: "center", padding: "48px 32px" }}>
        <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
          <XCircle style={{ width: 36, height: 36, color: "#dc2626" }} />
        </div>
        <h1 style={{ ...S.h1, marginBottom: 8 }}>{docLabel} Declined</h1>
        <p style={S.sub}>You&apos;ve declined {docLabel.toLowerCase()} <strong>{data.invoice_number}</strong>.</p>
        {(data.rejection_reason || rejectReason) && (
          <p style={{ ...S.sub, marginTop: 12, fontStyle: "italic" }}>&ldquo;{data.rejection_reason || rejectReason}&rdquo;</p>
        )}
        <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 28 }}>
          The business has been notified. If you change your mind, please contact {business?.name || "them"} directly.
        </p>
      </div>
    </div>
  );

  // ─── MAIN REVIEW / SIGNING / REJECTING ───
  return (
    <div style={S.page} data-testid="public-estimate-page">
      <div style={S.card}>
        {/* ── Header ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
          <div>
            {business?.logo_url && (
              <img src={business.logo_url.startsWith("http") ? business.logo_url : `${API}${business.logo_url}`} alt="" style={{ height: 36, objectFit: "contain", marginBottom: 10 }} />
            )}
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", margin: 0, fontFamily: S.fontHead }}>{data.company_name || business?.name}</h2>
            {business?.address && <p style={{ fontSize: 12, color: "#94a3b8", margin: "2px 0 0" }}>{business.address}</p>}
            {(business?.phone || business?.email) && (
              <p style={{ fontSize: 12, color: "#94a3b8", margin: "1px 0 0" }}>
                {[business.phone, business.email].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: isEstimate ? "#f97316" : "#3b82f6", marginBottom: 4 }}>{docLabelUpper}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", fontFamily: S.fontHead }}>{data.invoice_number}</div>
          </div>
        </div>

        {/* ── Customer + Dates ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28, paddingBottom: 20, borderBottom: "1px solid #e2e8f0" }}>
          <div>
            <p style={S.label}>{isEstimate ? "Prepared For" : "Billed To"}</p>
            <p style={{ fontSize: 15, fontWeight: 600, color: "#0f172a", margin: "4px 0 0" }}>{customer?.name}</p>
            {customer?.address && <p style={{ fontSize: 13, color: "#64748b", margin: "2px 0 0" }}>{customer.address}</p>}
            {customer?.email && <p style={{ fontSize: 13, color: "#64748b" }}>{customer.email}</p>}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ marginBottom: 8 }}>
              <p style={S.label}>Date</p>
              <p style={{ fontSize: 13, color: "#0f172a", margin: "3px 0 0", fontWeight: 500 }}>{fmtDate(data.created_at)}</p>
            </div>
            {isEstimate && data.valid_until && (
              <div>
                <p style={S.label}>Valid Until</p>
                <p style={{ fontSize: 13, color: "#0f172a", margin: "3px 0 0", fontWeight: 500 }}>{fmtDate(data.valid_until)}</p>
              </div>
            )}
            {!isEstimate && data.due_date && (
              <div>
                <p style={S.label}>Due Date</p>
                <p style={{ fontSize: 13, color: "#0f172a", margin: "3px 0 0", fontWeight: 500 }}>{fmtDate(data.due_date)}</p>
              </div>
            )}
          </div>
        </div>

        {/* ── Items Table ── */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: "0 16px", paddingBottom: 10, borderBottom: "2px solid #e2e8f0" }}>
            <span style={S.thLabel}>Description</span>
            <span style={{ ...S.thLabel, textAlign: "right" }}>Qty</span>
            <span style={{ ...S.thLabel, textAlign: "right" }}>Price</span>
            <span style={{ ...S.thLabel, textAlign: "right" }}>Total</span>
          </div>
          {(data.items || []).map((item, i) => {
            const lineTotal = (item.quantity || 1) * (item.unit_price || 0);
            const disc = lineTotal * ((item.discount || 0) / 100);
            return (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: "0 16px", padding: "12px 0", borderBottom: "1px solid #f1f5f9", alignItems: "start" }} data-testid={`item-row-${i}`}>
                <div>
                  <span style={{ fontSize: 14, color: "#0f172a", fontWeight: 500 }}>{item.description}</span>
                  {item.long_description ? (
                    <p style={{ fontSize: 12.5, color: "#64748b", margin: "3px 0 0", whiteSpace: "pre-wrap", lineHeight: 1.45 }} data-testid={`item-long-desc-${i}`}>
                      {item.long_description}
                    </p>
                  ) : null}
                </div>
                <span style={{ fontSize: 14, color: "#64748b", textAlign: "right" }}>{item.quantity}</span>
                <span style={{ fontSize: 14, color: "#64748b", textAlign: "right" }}>{fmt(item.unit_price)}</span>
                <span style={{ fontSize: 14, color: "#0f172a", textAlign: "right", fontWeight: 600 }}>
                  {fmt(lineTotal - disc)}
                </span>
              </div>
            );
          })}
        </div>

        {/* ── Totals ── */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, marginBottom: 24 }}>
          <div style={S.totalRow}><span>Subtotal</span><span style={{ fontWeight: 600 }}>{fmt(data.subtotal)}</span></div>
          {data.discount_total > 0 && (
            <div style={S.totalRow}><span>{data.discount_type === "flat" ? "Discount (flat)" : (data.discount_value || 0) > 0 ? `Discount (${data.discount_value}%)` : "Discount"}</span><span style={{ color: "#059669" }}>-{fmt(data.discount_total)}</span></div>
          )}
          {data.tax_rate > 0 && (data.non_taxable_subtotal || 0) > 0 && (
            <div style={S.totalRow}><span>Taxable Subtotal</span><span>{fmt(data.taxable_subtotal)}</span></div>
          )}
          {data.tax_rate > 0 && (
            <div style={S.totalRow}><span>Tax ({data.tax_rate}%)</span><span>{fmt(data.tax_amount)}</span></div>
          )}
          {data.credit_fee_enabled && (data.credit_fee_amount || 0) > 0 && (
            <div style={S.totalRow}>
              <span>{data.credit_fee_type === "percentage" ? `Credit Fee (${data.credit_fee_value}%)` : "Credit Fee"}</span>
              <span>{fmt(data.credit_fee_amount)}</span>
            </div>
          )}
          <div style={{ ...S.totalBox, marginTop: 8 }}>
            <span style={S.totalLabel}>{isEstimate ? "Estimate Total" : "Total"}</span>
            <span style={S.totalValue}>{fmt(data.total)}</span>
          </div>
        </div>

        {/* ── Payment History (invoices) ── */}
        {!isEstimate && (
          <div style={{ marginBottom: 24 }} data-testid="public-payment-history">
            <p style={S.label}>Payment History</p>
            {(data.payment_history || []).length === 0 ? (
              <p style={{ fontSize: 12.5, color: "#94a3b8", margin: "6px 0 0" }}>No payments received yet.</p>
            ) : (
              <>
                <div style={{ marginTop: 8, border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
                  {data.payment_history.map((p, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 14px", background: i % 2 ? "#f8fafc" : "white" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: p.is_refund ? "#dc2626" : "#0f172a" }}>
                          {p.type}
                          <span style={{ fontWeight: 400, color: "#64748b" }}> · {p.method}</span>
                        </div>
                        <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 1 }}>
                          {p.date ? new Date(`${p.date}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : ""}
                          {p.status ? ` · ${p.status}` : ""}
                        </div>
                      </div>
                      <span style={{ fontSize: 13.5, fontWeight: 700, color: p.is_refund ? "#dc2626" : "#0f172a", whiteSpace: "nowrap" }}>
                        {p.amount < 0 ? "-" : ""}{fmt(Math.abs(p.amount))}
                      </span>
                    </div>
                  ))}
                </div>
                {data.payment_summary && (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, marginTop: 10, fontSize: 12.5, color: "#475569" }}>
                    <span>Total Paid: <strong>{fmt(data.payment_summary.amount_paid)}</strong></span>
                    {(data.payment_summary.total_refunded || 0) > 0 && (
                      <>
                        <span>Total Refunded: <strong style={{ color: "#dc2626" }}>-{fmt(data.payment_summary.total_refunded)}</strong></span>
                        <span>Net Paid: <strong>{fmt(data.payment_summary.net_paid)}</strong></span>
                      </>
                    )}
                    <span>Balance Due: <strong style={{ color: (data.payment_summary.balance_due || 0) <= 0.009 ? "#059669" : "#b45309" }}>{fmt(data.payment_summary.balance_due)}</strong></span>
                    <span>Payment Status: <strong>{data.payment_summary.payment_status}</strong></span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Notes ── */}
        {data.notes && (
          <div style={{ marginBottom: 20, padding: "14px 16px", borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
            <p style={S.label}>Notes</p>
            <p style={{ fontSize: 13, color: "#475569", margin: "4px 0 0", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{data.notes}</p>
          </div>
        )}
        {data.payment_terms && (
          <div style={{ marginBottom: 20, padding: "14px 16px", borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
            <p style={S.label}>Payment Terms</p>
            <p style={{ fontSize: 13, color: "#475569", margin: "4px 0 0" }}>{data.payment_terms}</p>
          </div>
        )}

        {/* ── SIGNATURE SECTION (signing state) ── */}
        {flowState === "signing" && (
          <div style={{ marginBottom: 20, animation: "fadeIn 0.3s ease" }} data-testid="signature-section">
            {/* Signer name + email — required for legal audit trail. */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <p style={{ ...S.label, marginBottom: 6 }}>Full Name *</p>
                <input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Your full name"
                  style={S.input}
                  data-testid="signer-name-input"
                />
              </div>
              <div>
                <p style={{ ...S.label, marginBottom: 6 }}>Email *</p>
                <input
                  type="email"
                  value={signerEmail}
                  onChange={(e) => setSignerEmail(e.target.value)}
                  placeholder="you@example.com"
                  style={S.input}
                  data-testid="signer-email-input"
                />
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <p style={{ ...S.label, margin: 0, display: "flex", alignItems: "center", gap: 6 }}>
                <PenLine style={{ width: 14, height: 14 }} /> Sign Below to {isEstimate ? "Approve" : "Confirm"}
              </p>
              <button onClick={clearSig} style={S.clearBtn} data-testid="clear-signature-btn">
                <Eraser style={{ width: 14, height: 14 }} /> Clear
              </button>
            </div>
            <div style={{ border: "2px dashed #cbd5e1", borderRadius: 12, background: "#fafbfd", overflow: "hidden", position: "relative" }}>
              <SignatureCanvas
                ref={sigRef}
                penColor="#0f172a"
                canvasProps={{ style: { width: "100%", height: 160 }, "data-testid": "signature-canvas" }}
                onEnd={() => setHasSigned(true)}
              />
              {!hasSigned && (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                  <span style={{ fontSize: 14, color: "#cbd5e1", fontWeight: 500 }}>Draw your signature here</span>
                </div>
              )}
            </div>
            {/* Legal compliance text + audit hint */}
            <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0", display: "flex", gap: 10 }}>
              <ShieldCheck style={{ width: 18, height: 18, color: "#0f172a", flexShrink: 0, marginTop: 2 }} />
              <p style={{ fontSize: 11, color: "#64748b", margin: 0, lineHeight: 1.55 }}>
                By signing, you agree that your electronic signature is the legal equivalent of your handwritten signature.
                Your IP address, browser, and a timestamp will be recorded for audit purposes.
              </p>
            </div>
          </div>
        )}

        {/* ── REJECT REASON SECTION ── */}
        {flowState === "rejecting" && (
          <div style={{ marginBottom: 20, animation: "fadeIn 0.3s ease" }} data-testid="reject-section">
            <p style={{ ...S.label, marginBottom: 8 }}>Reason for Declining (optional)</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Let the business know why you're declining..."
              style={S.textarea}
              data-testid="reject-reason-input"
            />
          </div>
        )}

        {/* ── ERROR ── */}
        {error && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#dc2626" }}>{error}</div>
        )}

        {/* ── DESKTOP ACTION BUTTONS ── */}
        {canSign && (
          <div className="hidden sm:flex" style={{ justifyContent: "flex-end", gap: 12, marginTop: 32, paddingTop: 24, borderTop: "1px solid #e2e8f0" }}>
            {flowState === "review" && (
              <>
                {isEstimate && (
                  <button onClick={() => setFlowState("rejecting")} style={S.btnSecondary} data-testid="reject-estimate-btn">Decline {docLabel}</button>
                )}
                <button onClick={() => setFlowState("signing")} style={{ ...S.btnPrimary, background: primary }} data-testid="approve-estimate-btn">
                  {isEstimate ? "Approve & Sign" : "Sign Invoice"}
                </button>
              </>
            )}
            {flowState === "signing" && (
              <>
                <button onClick={() => { setFlowState("review"); clearSig(); setError(null); }} style={S.btnSecondary}>Back</button>
                <button onClick={handleApprove} disabled={!hasSigned || !signerName.trim() || !signerEmail.trim()} style={{ ...S.btnPrimary, background: (hasSigned && signerName.trim() && signerEmail.trim()) ? "#059669" : "#94a3b8", cursor: (hasSigned && signerName.trim() && signerEmail.trim()) ? "pointer" : "not-allowed" }} data-testid="confirm-approve-btn">
                  {isEstimate ? "Confirm Approval" : "Confirm Signature"}
                </button>
              </>
            )}
            {flowState === "rejecting" && (
              <>
                <button onClick={() => setFlowState("review")} style={S.btnSecondary}>Back</button>
                <button onClick={handleReject} style={{ ...S.btnPrimary, background: "#dc2626" }} data-testid="confirm-reject-btn">Confirm Decline</button>
              </>
            )}
            {flowState === "submitting" && (
              <button disabled style={{ ...S.btnPrimary, background: "#94a3b8", cursor: "not-allowed", display: "flex", alignItems: "center", gap: 8 }}>
                <Loader2 className="w-4 h-4 animate-spin" /> Processing...
              </button>
            )}
          </div>
        )}

        <p style={{ textAlign: "center", fontSize: 11, color: "#cbd5e1", marginTop: 28 }}>Powered by Fieldz AI</p>
      </div>

      {/* ── MOBILE STICKY ACTION BAR ── */}
      {canSign && (
        <div className="sm:hidden" style={S.stickyBar} data-testid="mobile-action-bar">
          {flowState === "review" && (
            <>
              {isEstimate && (
                <button onClick={() => setFlowState("rejecting")} style={{ ...S.btnSecondary, flex: 1 }} data-testid="mobile-reject-btn">Decline</button>
              )}
              <button onClick={() => setFlowState("signing")} style={{ ...S.btnPrimary, flex: 2, background: primary }} data-testid="mobile-approve-btn">
                {isEstimate ? "Approve & Sign" : "Sign Invoice"}
              </button>
            </>
          )}
          {flowState === "signing" && (
            <>
              <button onClick={() => { setFlowState("review"); clearSig(); setError(null); }} style={{ ...S.btnSecondary, flex: 1 }}>Back</button>
              <button onClick={handleApprove} disabled={!hasSigned || !signerName.trim() || !signerEmail.trim()} style={{ ...S.btnPrimary, flex: 2, background: (hasSigned && signerName.trim() && signerEmail.trim()) ? "#059669" : "#94a3b8", cursor: (hasSigned && signerName.trim() && signerEmail.trim()) ? "pointer" : "not-allowed" }} data-testid="mobile-confirm-approve-btn">
                {isEstimate ? "Confirm Approval" : "Confirm Signature"}
              </button>
            </>
          )}
          {flowState === "rejecting" && (
            <>
              <button onClick={() => setFlowState("review")} style={{ ...S.btnSecondary, flex: 1 }}>Back</button>
              <button onClick={handleReject} style={{ ...S.btnPrimary, flex: 2, background: "#dc2626" }} data-testid="mobile-confirm-reject-btn">Confirm Decline</button>
            </>
          )}
          {flowState === "submitting" && (
            <button disabled style={{ ...S.btnPrimary, flex: 1, background: "#94a3b8", cursor: "not-allowed", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Processing...
            </button>
          )}
        </div>
      )}

      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────
const S = {
  fontHead: "'Outfit', 'Inter', system-ui, sans-serif",
  fontBody: "'IBM Plex Sans', 'Inter', system-ui, sans-serif",
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "24px 12px 120px",
    background: "#f1f5f9",
    fontFamily: "'IBM Plex Sans', 'Inter', system-ui, sans-serif",
  },
  center: { display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh", width: "100%" },
  card: {
    width: "100%",
    maxWidth: 680,
    background: "white",
    borderRadius: 16,
    padding: "36px 28px",
    boxShadow: "0 8px 30px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.06)",
    border: "1px solid #e2e8f0",
  },
  h1: { fontSize: 24, fontWeight: 700, color: "#0f172a", fontFamily: "'Outfit', system-ui, sans-serif", margin: 0 },
  sub: { fontSize: 14, color: "#64748b", lineHeight: 1.6, margin: 0 },
  label: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#94a3b8", margin: 0 },
  thLabel: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#94a3b8" },
  totalRow: { display: "flex", justifyContent: "space-between", width: 240, fontSize: 14, color: "#475569" },
  totalBox: {
    display: "flex", justifyContent: "space-between", alignItems: "center", width: 240,
    padding: "14px 16px", borderRadius: 10, background: "#0f172a",
  },
  totalLabel: { fontSize: 12, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" },
  totalValue: { fontSize: 22, fontWeight: 800, color: "white", fontFamily: "'Outfit', system-ui, sans-serif" },
  btnPrimary: {
    padding: "13px 24px", border: "none", borderRadius: 10, color: "white",
    fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
    transition: "all 0.15s ease",
  },
  btnSecondary: {
    padding: "13px 24px", background: "white", border: "1px solid #e2e8f0", borderRadius: 10,
    color: "#475569", fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
    transition: "all 0.15s ease",
  },
  stickyBar: {
    position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 50,
    display: "flex", gap: 10, padding: "14px 16px",
    background: "rgba(255,255,255,0.92)", backdropFilter: "blur(16px) saturate(1.5)",
    borderTop: "1px solid #e2e8f0", boxShadow: "0 -10px 40px rgba(0,0,0,0.05)",
  },
  clearBtn: {
    display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600,
    color: "#94a3b8", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit",
  },
  textarea: {
    width: "100%", padding: "12px 14px", fontSize: 14, border: "1.5px solid #e2e8f0",
    borderRadius: 10, outline: "none", background: "#fafbfd", fontFamily: "inherit",
    resize: "vertical", minHeight: 80, boxSizing: "border-box",
  },
  input: {
    width: "100%", padding: "10px 12px", fontSize: 14, border: "1.5px solid #e2e8f0",
    borderRadius: 8, outline: "none", background: "#fafbfd", fontFamily: "inherit",
    boxSizing: "border-box",
  },
};

export default PublicEstimateView;
